import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Pusher from 'pusher-js';
import {
  FaUser,
  FaUserShield,
  FaSpinner,
  FaComments,
  FaPaperPlane,
  FaArrowLeft,
  FaTrashAlt,
  FaSearch,
  FaHeadset,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../services/server/axios';
import { withLastMessages, setCachedLastMessage } from '../../../services/chat/lastMessages';
import './Chat.scss';

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}') || {};
  } catch {
    return {};
  }
};

const broadcastUnread = (list) => {
  const total = (list || []).reduce((sum, c) => sum + (Number(c.unread) || 0), 0);
  window.dispatchEvent(new CustomEvent('mf-chat:unread', { detail: total }));
};

// Most recent activity first.
const convTime = (c) =>
  new Date(c.last_message?.created_at || c.updated_at || 0).getTime() || 0;

const sortConversations = (list) =>
  (list || []).slice().sort((a, b) => convTime(b) - convTime(a) || b.id - a.id);

const RoleIcon = ({ userRole, className }) =>
  userRole === 'admin' || userRole === 'superadmin' ? (
    <FaUserShield className={className} />
  ) : (
    <FaUser className={className} />
  );

const Avatar = ({ src, userRole, size = 40 }) => (
  <span className="mf-chat__avatar" style={{ width: size, height: size }}>
    {src ? <img src={src} alt="" /> : <RoleIcon userRole={userRole} />}
  </span>
);

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const Chat = () => {
  const { t } = useTranslation();
  const role = localStorage.getItem('role');
  const isStaff = role === 'admin' || role === 'superadmin';

  const [currentUser, setCurrentUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [mobileListOpen, setMobileListOpen] = useState(isStaff);

  const messagesBoxRef = useRef(null);
  const composerRef = useRef(null);
  const pusherRef = useRef(null);
  const activeConvRef = useRef(null);
  const conversationsRef = useRef([]);

  useEffect(() => {
    activeConvRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const tt = useCallback((key, fallback) => t(key, { defaultValue: fallback }), [t]);

  /* ---------------------------------------------------------------- data */

  const fetchConversations = useCallback(async ({ autoSelect = false } = {}) => {
    try {
      const { data } = await axiosInstance.get('/conversations');
      const incoming = data.conversations || [];

      // Keep any preview we already resolved for a conversation.
      let list = sortConversations(
        incoming.map((c) => {
          if (c.last_message) return c;
          const prev = conversationsRef.current.find((p) => p.id === c.id);
          return prev?.last_message
            ? { ...c, last_message: prev.last_message, updated_at: prev.updated_at || c.updated_at }
            : c;
        }),
      );
      setConversations(list);
      broadcastUnread(list);

      if (autoSelect && !activeConvRef.current && !isStaff && list.length > 0) {
        setActiveConversation(list[0]);
      }

      // Reconstruct missing previews from the threads in the background
      // (cached for the session) so the UI is not blocked on first paint.
      const beforeById = new Map(list.map((c) => [c.id, c.last_message]));
      withLastMessages(list).then((enriched) => {
        const sorted = sortConversations(enriched);
        if (sorted.some((c) => beforeById.get(c.id) !== c.last_message)) {
          setConversations(sorted);
          broadcastUnread(sorted);
        }
      });
      return list;
    } catch (err) {
      console.error('conversations', err);
      setError(tt('errorConversations', 'Unable to load conversations.'));
      return [];
    }
  }, [isStaff, tt]);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const { data } = await axiosInstance.get(`/messages/${conversationId}`);
      const list = data.messages || [];
      setMessages(list);

      // Backfill the list preview from the thread when the API omits it.
      const last = list[list.length - 1];
      if (last) {
        const lm = {
          body: last.body,
          sender_id: last.sender?.id ?? last.sender_id,
          created_at: last.created_at,
        };
        setCachedLastMessage(conversationId, lm);
        setConversations((prev) =>
          sortConversations(
            prev.map((c) =>
              c.id === conversationId
                ? { ...c, last_message: lm, updated_at: lm.created_at || c.updated_at }
                : c,
            ),
          ),
        );
      }
    } catch (err) {
      console.error('messages', err);
      setError(tt('errorMessages', 'Unable to load messages.'));
    }
  }, [tt]);

  const markAsRead = useCallback(async (conversationId) => {
    try {
      await axiosInstance.post(`/conversations/${conversationId}/read`);
      setConversations((prev) => {
        const next = prev.map((c) => (c.id === conversationId ? { ...c, unread: 0 } : c));
        broadcastUnread(next);
        return next;
      });
    } catch (err) {
      console.error('read', err);
    }
  }, []);

  /* ---------------------------------------------------------------- bootstrap */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/user');
        if (cancelled) return;
        const stored = getStoredUser();
        setCurrentUser({
          ...data,
          name: data.name || data.username || stored.username,
          role: role || stored.roles?.[0]?.name || 'client',
          avatar: data.photo_profil || stored.photo_profil || null,
        });
      } catch (err) {
        console.error('user', err);
        if (!cancelled) {
          setError(tt('errorUser', 'Failed to fetch user data.'));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [role, tt]);

  useEffect(() => {
    if (!currentUser) return undefined;

    (async () => {
      await fetchConversations({ autoSelect: true });
      setLoading(false);
    })();

    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY || '38f74624900631a94686', {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'mt1',
      forceTLS: true,
      channelAuthorization: {
        endpoint: `${process.env.REACT_APP_BASE_URL}/pusher/auth`,
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      },
    });
    pusherRef.current = pusher;

    const channel = pusher.subscribe(`private-user-${currentUser.id}`);

    channel.bind('new-message', (incoming) => {
      const active = activeConvRef.current;
      const isActive = active && incoming.conversation_id === active.id;

      if (isActive) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === incoming.id)) return prev;
          const withoutTemp = prev.filter(
            (m) => !(m.temp && m.body === incoming.body && m.sender?.id === incoming.sender?.id),
          );
          return [...withoutTemp, incoming];
        });
        markAsRead(active.id);
      }

      // Patch the list straight from the event so the preview + ordering are
      // correct even if the API response has no `last_message` field.
      const mine = incoming.sender?.id === currentUser.id;
      const at = incoming.created_at || new Date().toISOString();
      setCachedLastMessage(incoming.conversation_id, {
        body: incoming.body,
        sender_id: incoming.sender?.id ?? incoming.sender_id,
        created_at: at,
      });
      setConversations((prev) => {
        const next = prev.map((c) =>
          c.id === incoming.conversation_id
            ? {
                ...c,
                last_message: {
                  body: incoming.body,
                  sender_id: incoming.sender?.id ?? incoming.sender_id,
                  created_at: at,
                },
                updated_at: at,
                unread: isActive || mine ? c.unread || 0 : (c.unread || 0) + 1,
              }
            : c,
        );
        const sorted = sortConversations(next);
        broadcastUnread(sorted);
        return sorted;
      });

      fetchConversations();
    });

    channel.bind('message-deleted', (data) => {
      setMessages((prev) => prev.filter((m) => m.id !== data.message_id));
      fetchConversations();
    });

    channel.bind('conversation-closed', (data) => {
      setConversations((prev) => prev.filter((c) => c.id !== data.conversation_id));
      if (activeConvRef.current?.id === data.conversation_id) {
        setActiveConversation(null);
        setMessages([]);
      }
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe(`private-user-${currentUser.id}`);
      pusher.disconnect();
      pusherRef.current = null;
    };
  }, [currentUser, fetchConversations, markAsRead]);

  useEffect(() => {
    if (!activeConversation) return;
    fetchMessages(activeConversation.id);
    markAsRead(activeConversation.id);
  }, [activeConversation, fetchMessages, markAsRead]);

  // Keep the thread pinned to the newest message.
  useLayoutEffect(() => {
    const box = messagesBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages, activeConversation]);

  useEffect(() => {
    const box = messagesBoxRef.current;
    if (!box) return undefined;
    const jump = () => { box.scrollTop = box.scrollHeight; };
    const raf = requestAnimationFrame(jump);
    const timer = setTimeout(jump, 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [messages, activeConversation]);

  /* ---------------------------------------------------------------- actions */

  const startConversation = async () => {
    try {
      const { data } = await axiosInstance.post('/conversations');
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === data.conversation.id);
        return exists ? prev : [data.conversation, ...prev];
      });
      setActiveConversation(data.conversation);
      setMobileListOpen(false);
    } catch (err) {
      console.error('create conversation', err);
      setError(tt('errorCreateConversation', 'Unable to start the conversation.'));
    }
  };

  const sendMessage = async () => {
    const body = inputMessage.trim();
    if (!body || !activeConversation || sending) return;

    setSending(true);
    const temp = {
      id: `temp-${Date.now()}`,
      body,
      conversation_id: activeConversation.id,
      created_at: new Date().toISOString(),
      sender: {
        id: currentUser.id,
        name: currentUser.name,
        role: currentUser.role,
        avatar: currentUser.avatar,
      },
      temp: true,
    };
    setMessages((prev) => [...prev, temp]);
    setInputMessage('');
    if (composerRef.current) composerRef.current.style.height = 'auto';

    try {
      const { data } = await axiosInstance.post('/messages', {
        body,
        conversation_id: activeConversation.id,
      });
      const saved = data.message || {};
      setMessages((prev) => {
        const next = prev.filter((m) => m.id !== temp.id);
        return saved.id && next.some((m) => m.id === saved.id) ? next : [...next, { ...temp, ...saved, temp: false }];
      });
      const lm = {
        body,
        sender_id: currentUser.id,
        created_at: saved.created_at || temp.created_at,
      };
      setCachedLastMessage(activeConversation.id, lm);
      setConversations((prev) =>
        sortConversations(
          prev.map((c) =>
            c.id === activeConversation.id
              ? { ...c, last_message: lm, updated_at: lm.created_at }
              : c,
          ),
        ),
      );
      fetchConversations();
    } catch (err) {
      console.error('send', err);
      setMessages((prev) => prev.filter((m) => m.id !== temp.id));
      setInputMessage(body);
      setError(tt('errorSend', 'Message could not be sent.'));
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async (messageId) => {
    const snapshot = messages;
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    try {
      await axiosInstance.delete(`/messages/${messageId}`);
      fetchConversations();
    } catch (err) {
      console.error('delete', err);
      setMessages(snapshot);
      setError(tt('errorDelete', 'Message could not be deleted.'));
    }
  };

  const handleInput = (e) => {
    setInputMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openConversation = (conversation) => {
    setActiveConversation(conversation);
    setMobileListOpen(false);
  };

  /* ---------------------------------------------------------------- helpers */

  const formatTime = (value) =>
    new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const dayLabel = (value) => {
    const date = new Date(value);
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(date, now)) return tt('today', 'Today');
    if (sameDay(date, yesterday)) return tt('yesterday', 'Yesterday');
    return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((c) =>
      (c.title || '').toLowerCase().includes(term) ||
      (c.last_message?.body || '').toLowerCase().includes(term),
    );
  }, [conversations, search]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    messages.forEach((message) => {
      const label = dayLabel(message.created_at);
      const last = groups.at(-1);
      if (last?.label === label) last.items.push(message);
      else groups.push({ label, items: [message] });
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const headerTitle = isStaff
    ? activeConversation?.title || tt('supportChat', 'Support Chat')
    : tt('supportChat', 'Support Chat');

  let headerSubtitle = tt('supportOnline', 'We usually reply within a few minutes');
  if (isStaff) {
    headerSubtitle =
      activeConversation?.participant?.role === 'superadmin'
        ? tt('superAdmin', 'Super Admin')
        : tt('client', 'Client');
  }

  /* ---------------------------------------------------------------- render */

  if (loading) {
    return (
      <div className="mf-chat mf-chat--state">
        <FaSpinner className="mf-chat__spin" />
        <p>{tt('loading', 'Loading chat...')}</p>
      </div>
    );
  }

  if (error && !currentUser) {
    return (
      <div className="mf-chat mf-chat--state">
        <p className="mf-chat__error-text">{error}</p>
        <button type="button" className="mf-chat__btn" onClick={() => window.location.reload()}>
          {tt('retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className={`mf-chat${isStaff ? ' mf-chat--staff' : ''}`}>
      {isStaff && (
        <aside className={`mf-chat__sidebar${mobileListOpen ? ' is-open' : ''}`}>
          <div className="mf-chat__sidebar-head">
            <h5>{tt('conversations', 'Conversations')}</h5>
            <div className="mf-chat__search">
              <FaSearch />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tt('searchConversations', 'Search…')}
              />
            </div>
          </div>

          <div className="mf-chat__conv-list">
            {filteredConversations.length === 0 ? (
              <p className="mf-chat__empty-hint">{tt('noConversation', 'No active conversation')}</p>
            ) : (
              filteredConversations.map((conversation) => (
                <button
                  type="button"
                  key={conversation.id}
                  className={`mf-chat__conv${
                    activeConversation?.id === conversation.id ? ' is-active' : ''
                  }`}
                  onClick={() => openConversation(conversation)}
                >
                  <Avatar
                    src={conversation.participant?.avatar}
                    userRole={conversation.participant?.role}
                  />
                  <span className="mf-chat__conv-body">
                    <span className="mf-chat__conv-top">
                      <span className="mf-chat__conv-name">
                        {conversation.with_superadmin
                          ? tt('supportChat', 'Support Chat')
                          : conversation.title}
                      </span>
                      {conversation.last_message && (
                        <span className="mf-chat__conv-time">
                          {formatTime(conversation.last_message.created_at)}
                        </span>
                      )}
                    </span>
                    <span className="mf-chat__conv-preview">
                      {conversation.last_message?.body
                        || (conversation.unread > 0
                          ? tt('newMessage', 'New message')
                          : tt('noMessages', 'No messages yet.'))}
                    </span>
                  </span>
                  {conversation.unread > 0 && (
                    <span className="mf-chat__badge">{conversation.unread}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </aside>
      )}

      <section className="mf-chat__main">
        {activeConversation ? (
          <>
            <header className="mf-chat__header">
              {isStaff && (
                <button
                  type="button"
                  className="mf-chat__icon-btn mf-chat__back"
                  onClick={() => setMobileListOpen(true)}
                  aria-label={tt('back', 'Back')}
                >
                  <FaArrowLeft />
                </button>
              )}
              <Avatar
                src={isStaff ? activeConversation.participant?.avatar : null}
                userRole={isStaff ? activeConversation.participant?.role : 'admin'}
              />
              <div className="mf-chat__header-info">
                <strong>{headerTitle}</strong>
                <span>{headerSubtitle}</span>
              </div>
            </header>

            <div className="mf-chat__messages" ref={messagesBoxRef}>
              {messages.length === 0 ? (
                <div className="mf-chat__messages-empty">
                  <FaComments />
                  <p>{tt('noMessages', 'No messages yet.')}</p>
                </div>
              ) : (
                groupedMessages.map((group) => (
                  <div key={group.label} className="mf-chat__day-group">
                    <span className="mf-chat__day">{group.label}</span>
                    {group.items.map((message) => {
                      const own = message.sender?.id === currentUser?.id;
                      return (
                        <div
                          key={message.id}
                          className={`mf-chat__row${own ? ' is-own' : ''}`}
                        >
                          {!own && (
                            <Avatar
                              src={message.sender?.avatar}
                              userRole={message.sender?.role}
                              size={32}
                            />
                          )}
                          <div
                            className={`mf-chat__bubble${own ? ' is-own' : ''}${
                              message.temp ? ' is-temp' : ''
                            }`}
                          >
                            {!own && (
                              <span className="mf-chat__bubble-sender">
                                <RoleIcon userRole={message.sender?.role} />
                                {message.sender?.name}
                              </span>
                            )}
                            <p>{message.body}</p>
                            <span className="mf-chat__bubble-meta">
                              {formatTime(message.created_at)}
                              {message.temp && ` · ${tt('sending', 'Sending...')}`}
                            </span>
                            {own && !message.temp && (
                              <button
                                type="button"
                                className="mf-chat__delete"
                                onClick={() => deleteMessage(message.id)}
                                aria-label={tt('delete', 'Delete')}
                              >
                                <FaTrashAlt />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {error && <div className="mf-chat__toast" onAnimationEnd={() => setError(null)}>{error}</div>}

            <div className="mf-chat__composer">
              <textarea
                ref={composerRef}
                rows={1}
                value={inputMessage}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder={tt('placeholder', 'Type a message...')}
                disabled={sending}
              />
              <button
                type="button"
                className="mf-chat__send"
                onClick={sendMessage}
                disabled={!inputMessage.trim() || sending}
                aria-label={tt('sendMessage', 'Send message')}
              >
                {sending ? <FaSpinner className="mf-chat__spin" /> : <FaPaperPlane />}
                <span className="mf-chat__send-label">{tt('send', 'Send')}</span>
              </button>
            </div>
          </>
        ) : (
          <div className="mf-chat__welcome">
            <span className="mf-chat__welcome-icon">
              <FaHeadset />
            </span>
            <h3>{tt('noConversation', 'No active conversation')}</h3>
            <p>{tt('noConversationDesc', 'You don’t have any active chats yet.')}</p>
            {!isStaff && (
              <button type="button" className="mf-chat__btn" onClick={startConversation}>
                {tt('startSupport', 'Start Support Chat')}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Chat;
